<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯模組' : '新增模組' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel" :disabled="loading">
          取消
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="loading || codeValidation.isChecking || (!isEdit && (!codeValidation.checked || !codeValidation.isValid))"
          :title="!isEdit && (!codeValidation.checked || !codeValidation.isValid) ? '請先完成模組代碼驗證' : ''"
          @click="handleSubmit"
        >
          <span v-if="loading">{{ isEdit ? '更新中...' : '建立中...' }}</span>
          <span v-else>{{ isEdit ? '更新模組' : '建立模組' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="create-module-container">

    <div class="form-container">
      <form @submit.prevent="handleSubmit">
        <div class="form-section">
          <h2 class="section-title">基本資訊</h2>

          <div class="form-row">
            <div class="form-group">
              <label for="moduleCode" class="required">模組代碼</label>
              <input
                id="moduleCode"
                v-model="form.moduleCode"
                type="text"
                class="form-control"
                :class="{
                  'is-invalid': errors.moduleCode || (codeValidation.checked && !codeValidation.isValid && !codeValidation.isChecking),
                  'is-valid': codeValidation.isValid && !codeValidation.isChecking
                }"
                placeholder="請輸入模組代碼（例如：MEMBER_MANAGEMENT, COUPON_SYSTEM）"
                :disabled="isEdit || codeValidation.isChecking"
                @input="handleModuleCodeInput"
                @blur="handleModuleCodeBlur"
                required
              />
              <div v-if="errors.moduleCode" class="invalid-feedback">
                {{ errors.moduleCode }}
              </div>
              <div v-else-if="codeValidation.isChecking" class="validation-feedback checking">
                <i class="ri-loader-4-line spin"></i> 檢查中...
              </div>
              <div v-else-if="codeValidation.checked && codeValidation.message && !codeValidation.isValid" class="validation-feedback invalid">
                <i class="ri-close-circle-line"></i> {{ codeValidation.message }}
              </div>
              <div v-else-if="codeValidation.isValid" class="validation-feedback valid">
                <i class="ri-check-line"></i> {{ codeValidation.message }}
              </div>
              <p class="form-hint">模組代碼一旦建立後無法修改，輸入時會自動轉換為大寫，可使用字母、數字和底線</p>
            </div>

            <div class="form-group">
              <label for="moduleName" class="required">模組名稱</label>
              <input
                id="moduleName"
                v-model="form.moduleName"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.moduleName }"
                placeholder="請輸入模組名稱（例如：會員管理、優惠券系統）"
                required
              />
              <div v-if="errors.moduleName" class="invalid-feedback">
                {{ errors.moduleName }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="moduleNameEn">模組名稱（英文）</label>
              <input
                id="moduleNameEn"
                v-model="form.moduleNameEn"
                type="text"
                class="form-control"
                placeholder="請輸入英文名稱（例如：Member Management）"
              />
            </div>

            <div class="form-group">
              <label class="required">適用帳號</label>
              <div class="checkbox-group">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    class="form-checkbox"
                    :checked="form.visibleAccountTypes.includes('system')"
                    @change="toggleAccountType('system', $event.target.checked)"
                  />
                  <span>系統</span>
                </label>
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    class="form-checkbox"
                    :checked="form.visibleAccountTypes.includes('platform')"
                    @change="toggleAccountType('platform', $event.target.checked)"
                  />
                  <span>平台</span>
                </label>
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    class="form-checkbox"
                    :checked="form.visibleAccountTypes.includes('client')"
                    @change="toggleAccountType('client', $event.target.checked)"
                  />
                  <span>客戶</span>
                </label>
              </div>
              <div v-if="errors.visibleAccountTypes" class="invalid-feedback" style="display:block">
                {{ errors.visibleAccountTypes }}
              </div>
              <div class="form-hint-important">
                <i class="ri-alert-line"></i>
                <span>系統與平台可同時勾選；勾平台會自動勾系統。客戶不可與系統／平台同時選擇。</span>
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full-width">
              <label for="description" class="required">模組描述</label>
              <textarea
                id="description"
                v-model="form.description"
                class="form-control"
                :class="{ 'is-invalid': errors.description }"
                rows="3"
                placeholder="請輸入模組描述"
                required
              ></textarea>
              <div v-if="errors.description" class="invalid-feedback">
                {{ errors.description }}
              </div>
              <p class="form-hint">便於設定角色與方案時容易辨別模組功能</p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full-width">
              <label for="descriptionEn">模組描述（英文）</label>
              <textarea
                id="descriptionEn"
                v-model="form.descriptionEn"
                class="form-control"
                rows="3"
                placeholder="請輸入英文描述"
              ></textarea>
            </div>
          </div>

          <!-- 價格設定（僅選購模組） -->
          <div v-if="isClientModule" class="form-row">
            <div class="form-group">
              <div class="checkbox-group">
                <label class="checkbox-label">
                  <input
                    id="isOptional"
                    v-model="form.isOptional"
                    type="checkbox"
                    class="form-checkbox"
                  />
                  <span>選購模組（僅客戶帳號，需加購才出現在客戶選單）</span>
                </label>
              </div>
            </div>
          </div>
          <div v-if="form.isOptional" class="form-row">
            <div class="form-group">
              <label for="monthlyPrice">月費（NT$）</label>
              <input
                id="monthlyPrice"
                v-model.number="form.monthlyPrice"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.monthlyPrice }"
                placeholder="請輸入月費"
                min="0"
                step="1"
                @input="form.monthlyPrice = form.monthlyPrice ? Math.floor(Number(form.monthlyPrice)) : null"
              />
              <div v-if="errors.monthlyPrice" class="invalid-feedback">
                {{ errors.monthlyPrice }}
              </div>
              <p class="form-hint">選購模組的月費價格</p>
            </div>

            <div class="form-group">
              <label for="yearlyPrice">年費（NT$）</label>
              <input
                id="yearlyPrice"
                v-model.number="form.yearlyPrice"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.yearlyPrice }"
                placeholder="請輸入年費"
                min="0"
                step="1"
                @input="form.yearlyPrice = form.yearlyPrice ? Math.floor(Number(form.yearlyPrice)) : null"
              />
              <div v-if="errors.yearlyPrice" class="invalid-feedback">
                {{ errors.yearlyPrice }}
              </div>
              <p class="form-hint">選購模組的年費價格（可享折扣）</p>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h2 class="section-title">顯示設定</h2>

          <div class="form-row">
            <div class="form-group">
              <label for="icon">圖示</label>
              <IconPicker v-model="form.icon" />
              <p class="form-hint">選擇模組的視覺圖示</p>
            </div>

            <div class="form-group">
              <label for="sortOrder">排序順序</label>
              <input
                id="sortOrder"
                v-model.number="form.sortOrder"
                type="number"
                class="form-control"
                min="0"
                placeholder="請輸入排序順序"
              />
              <p class="form-hint">數字越小越前面</p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="status" class="required">狀態</label>
              <select
                id="status"
                v-model="form.status"
                class="form-control"
                required
              >
                <option value="active">啟用</option>
                <option value="inactive">停用</option>
              </select>
              <div class="form-hint-important">
                <i class="ri-alert-line"></i>
                <span>停用時，模組不會顯示在選單中，也不會被包含在權限查詢中</span>
              </div>
            </div>

            <div class="form-group">
              <!-- 空白欄位，保持對齊 -->
            </div>
          </div>
        </div>

        <!-- 程式功能設定 -->
        <div class="form-section">
          <h2 class="section-title">程式功能設定</h2>
          
          <div class="programs-management">
            <div class="programs-header">
              <p class="programs-description">
                設定此模組可以使用的程式功能。添加、編輯權限、移除與拖拉排序會先留在畫面上，按「{{ isEdit ? '更新模組' : '建立模組' }}」後才寫入。
              </p>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                @click="openAddProgramsDialog"
                :disabled="loadingPrograms || programsLoadFailed"
              >
                <i class="ri-add-line"></i> 添加程式
              </button>
            </div>

            <div v-if="loadingPrograms" class="loading-state">
              <i class="ri-loader-4-line spin"></i> 載入中...
            </div>

            <div v-else-if="programsLoadFailed" class="empty-state">
              <i class="ri-error-warning-line"></i>
              <p>載入程式功能失敗，請重新整理後再編輯，以免覆寫時清空既有設定</p>
            </div>

            <div v-else-if="associatedPrograms.length === 0" class="empty-state">
              <i class="ri-folder-open-line"></i>
              <p>尚未設定任何程式功能</p>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                @click="openAddProgramsDialog"
              >
                <i class="ri-add-line"></i> 添加程式
              </button>
            </div>

            <div v-else class="programs-list">
              <div
                v-for="(program, index) in associatedPrograms"
                :key="program.id"
                class="program-item"
                draggable="true"
                :class="{ 'drag-over': programDragOverIndex === index, dragging: programDragIndex === index }"
                @dragstart="onProgramDragStart(index)"
                @dragover.prevent="onProgramDragOver(index)"
                @dragleave="onProgramDragLeave"
                @drop="onProgramDrop(index)"
                @dragend="onProgramDragEnd"
              >
                <div class="program-info">
                  <i class="ri-drag-move-2-line drag-handle" title="拖拉排序"></i>
                  <i :class="program.icon || 'ri-file-line'" class="program-icon"></i>
                  <div class="program-details">
                    <div class="program-name">{{ program.programName }}</div>
                    <div class="program-meta">
                      <span class="program-code">{{ program.programCode }}</span>
                      <span class="program-path-small">{{ program.programPath }}</span>
                    </div>
                    <!-- 顯示程式功能描述 -->
                    <div v-if="program.description" class="program-description">
                      {{ program.description }}
                    </div>
                    <!-- 顯示此模組允許的權限 -->
                    <div class="program-allowed-permissions">
                      <div v-if="isAllPermissionsSelected(program)" class="all-perms-allowed">
                        <i class="ri-check-line"></i>
                        <span>允許所有功能</span>
                      </div>
                      <div v-else-if="program.allowedPermissions && program.allowedPermissions.length > 0" class="allowed-perms">
                        <span class="perms-label">允許功能：</span>
                        <span v-for="perm in program.allowedPermissions" :key="perm" class="perm-badge">
                          {{ formatPermissionName(perm) }}
                        </span>
                      </div>
                      <div v-else class="all-perms-allowed">
                        <i class="ri-check-line"></i>
                        <span>允許所有功能</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="program-actions">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    @click.stop="handleEditPermissions(program)"
                    title="編輯權限"
                  >
                    <i class="ri-shield-line"></i>
                    編輯權限
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-danger"
                    @click.stop="handleRemoveProgram(program.id)"
                  >
                    <i class="ri-delete-bin-line"></i>
                    移除
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger">
          {{ errorMessage }}
        </div>
      </form>
    </div>

    <!-- 添加程式對話框 -->
    <ProgramSelector
      :show="showAddProgramsDialog"
      title="添加程式功能"
      confirm-button-text="添加"
      :available-programs="availablePrograms"
      :loading="loadingAvailablePrograms"
      :saving="addingPrograms"
      :initial-selected="{}"
      @close="showAddProgramsDialog = false"
      @confirm="handleAddPrograms"
    />

    <!-- 編輯權限對話框 -->
    <div v-if="showEditPermissionsDialog" class="modal-overlay" @click.self="showEditPermissionsDialog = false">
      <div class="modal-dialog">
        <div class="modal-header">
          <h3 class="modal-title">編輯程式權限</h3>
          <button
            type="button"
            class="btn-close"
            @click="showEditPermissionsDialog = false"
          >
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="editingProgram" class="editing-program-info">
            <div class="info-row">
              <i :class="editingProgram.icon || 'ri-file-line'" class="program-icon-large"></i>
              <div>
                <div class="program-name-large">{{ editingProgram.programName }}</div>
                <div class="program-meta-large">
                  <span class="program-code">{{ editingProgram.programCode }}</span>
                  <span class="program-path-small">{{ editingProgram.programPath }}</span>
                </div>
                <!-- 顯示程式功能描述 -->
                <div v-if="editingProgram.description" class="program-description-large">
                  {{ editingProgram.description }}
                </div>
              </div>
            </div>

            <div class="permissions-editor">
              <div class="permissions-header">
                <span class="permissions-label">選擇允許的功能：</span>
                <button
                  type="button"
                  class="btn-link btn-sm"
                  @click="selectAllEditPermissions"
                >
                  全選
                </button>
              </div>

              <div v-if="editingProgram.permissions && editingProgram.permissions.length > 0" class="permissions-checkboxes">
                <label
                  v-for="perm in editingProgram.permissions"
                  :key="perm"
                  class="permission-checkbox-label"
                >
                  <input
                    type="checkbox"
                    :checked="editPermissionsSelected === null || (Array.isArray(editPermissionsSelected) && editPermissionsSelected.includes(perm))"
                    @change="handleEditPermissionToggle(perm)"
                    :disabled="perm.endsWith(':read') && Array.isArray(editPermissionsSelected) && editPermissionsSelected.length === 1 && editPermissionsSelected.includes(perm)"
                  />
                  <span class="permission-name">{{ formatPermissionName(perm) }}</span>
                </label>
              </div>
              <div v-else class="no-permissions">
                <i class="ri-information-line"></i>
                此程式沒有定義任何權限
              </div>

              <div class="permissions-hint">
                <i class="ri-information-line"></i>
                read 權限預設勾選且不可取消，點擊「允許全部功能」可允許此程式的所有功能
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="showEditPermissionsDialog = false"
          >
            取消
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="handleSavePermissions"
          >
            確定
          </button>
        </div>
      </div>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { moduleService } from '@/api/modules/modules.js';
import { applyAccountTypeToggle, getModuleVisibleAccountTypes, isModuleOptional } from '@/utils/moduleVisibility.js';
import { moduleProgramService } from '@/api/modules/modulePrograms.js';
import { programService } from '@/api/modules/programs.js';
import {
  appendProgramsToDraft,
  buildModuleProgramsPayload,
  reorderProgramsInDraft
} from '@/utils/moduleProgramsDraft.js';
import { showSuccess, showError } from '@/utils/swal.js';
import IconPicker from '@/components/IconPicker.vue';
import ProgramSelector from '@/components/ProgramSelector.vue';

export default {
  name: 'CreateModule',
  components: {
    IconPicker,
    ProgramSelector
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const errorMessage = ref('');

    // 程式功能管理相關
    const loadingPrograms = ref(false);
    const programsLoadFailed = ref(false);
    const associatedPrograms = ref([]);
    const showAddProgramsDialog = ref(false);
    const loadingAvailablePrograms = ref(false);
    const availablePrograms = ref([]);
    const addingPrograms = ref(false);
    const programDragIndex = ref(null);
    const programDragOverIndex = ref(null);
    
    // 編輯權限相關
    const showEditPermissionsDialog = ref(false);
    const editingProgram = ref(null);
    const editPermissionsSelected = ref([]);

    const form = reactive({
      moduleCode: '',
      moduleName: '',
      moduleNameEn: '',
      visibleAccountTypes: [],
      isOptional: false,
      description: '',
      descriptionEn: '',
      icon: '',
      sortOrder: 0,
      status: 'active',
      monthlyPrice: null,
      yearlyPrice: null,
    });

    const errors = reactive({
      moduleCode: '',
      moduleName: '',
      visibleAccountTypes: '',
      description: '',
    });

    const codeValidation = reactive({
      isChecking: false,
      isValid: false,
      checked: false, // 是否已觸發過驗證（離開欄位後才設為 true）
      message: ''
    });

    const isEdit = computed(() => !!route.params.id);
    const isClientModule = computed(() => form.visibleAccountTypes.length === 1 && form.visibleAccountTypes[0] === 'client');

    const toggleAccountType = (type, checked) => {
      form.visibleAccountTypes = applyAccountTypeToggle(form.visibleAccountTypes, type, checked);
      if (!isClientModule.value) {
        form.isOptional = false;
      }
    };

    watch(isClientModule, (isClient) => {
      if (!isClient) {
        form.isOptional = false;
      }
    });

    const checkCodeAvailability = async (code) => {
      if (!code || code.trim().length === 0) {
        codeValidation.isChecking = false;
        codeValidation.isValid = false;
        codeValidation.checked = true;
        codeValidation.message = '請輸入模組代碼';
        return;
      }

      if (!/^[A-Za-z0-9_]+$/.test(code)) {
        codeValidation.isChecking = false;
        codeValidation.isValid = false;
        codeValidation.checked = true;
        codeValidation.message = '模組代碼只能包含大小寫字母、數字和底線';
        return;
      }

      codeValidation.isChecking = true;
      codeValidation.isValid = false;
      codeValidation.message = '檢查中...';

      try {
        const response = await moduleService.checkCode(code);
        if (response && response.success) {
          codeValidation.isValid = !response.data.exists;
          codeValidation.message = response.data.exists ?
            '此模組代碼已被使用，請使用其他代碼' : '此代碼可以使用';
        } else {
          codeValidation.isValid = false;
          codeValidation.message = response?.message || '檢查模組代碼失敗';
        }
      } catch (error) {
        console.error('檢查模組代碼失敗:', error);
        codeValidation.isValid = false;
        codeValidation.message = error.message || '檢查模組代碼失敗';
      } finally {
        codeValidation.isChecking = false;
        codeValidation.checked = true;
      }
    };

    // 輸入時只做大寫轉換，不觸發驗證
    const handleModuleCodeInput = (event) => {
      const upperValue = event.target.value.toUpperCase();
      form.moduleCode = upperValue;
      // 若用戶重新輸入，重置驗證狀態（需要再次離開欄位才會驗證）
      codeValidation.isValid = false;
      codeValidation.checked = false;
      codeValidation.message = '';
    };

    // 離開輸入欄位時才觸發驗證
    const handleModuleCodeBlur = () => {
      if (!isEdit.value && form.moduleCode) {
        checkCodeAvailability(form.moduleCode);
      }
    };

    // 保留 handleCodeInput 以維持向後相容（實際上不再使用）
    const handleCodeInput = () => {};

    const validateForm = () => {
      let isValid = true;
      
      // 重置錯誤
      errors.moduleCode = '';
      errors.moduleName = '';
      errors.visibleAccountTypes = '';
      errors.description = '';

      if (!form.moduleCode || form.moduleCode.trim().length === 0) {
        errors.moduleCode = '請輸入模組代碼';
        isValid = false;
      } else if (!/^[A-Za-z0-9_]+$/.test(form.moduleCode)) {
        errors.moduleCode = '模組代碼只能包含大小寫字母、數字和底線';
        isValid = false;
      }

      if (!form.moduleName || form.moduleName.trim().length === 0) {
        errors.moduleName = '請輸入模組名稱';
        isValid = false;
      }

      if (!form.visibleAccountTypes.length) {
        errors.visibleAccountTypes = '請選擇適用帳號';
        isValid = false;
      }

      if (!form.description || form.description.trim().length === 0) {
        errors.description = '請輸入模組描述';
        isValid = false;
      }

      return isValid;
    };

    const fetchModule = async () => {
      if (!route.params.id) return;

      loading.value = true;
      try {
        const response = await moduleService.getById(route.params.id);
        
        // axios 攔截器已經返回 response.data，所以這裡直接檢查 success
        if (response && response.success && response.data) {
          const module = response.data;
          form.moduleCode = module.moduleCode || '';
          form.moduleName = module.moduleName || '';
          form.moduleNameEn = module.moduleNameEn || '';
          form.visibleAccountTypes = getModuleVisibleAccountTypes(module);
          form.isOptional = isModuleOptional(module);
          form.description = module.description || '';
          form.descriptionEn = module.descriptionEn || '';
          form.icon = module.icon || '';
          form.sortOrder = module.sortOrder || 0;
          form.status = module.status || 'active';
          form.monthlyPrice = module.monthlyPrice !== undefined && module.monthlyPrice !== null ? Math.floor(Number(module.monthlyPrice)) : null;
          form.yearlyPrice = module.yearlyPrice !== undefined && module.yearlyPrice !== null ? Math.floor(Number(module.yearlyPrice)) : null;

          // 載入關聯的程式
          await fetchAssociatedPrograms();
        } else {
          await showError('獲取失敗', response?.message || '獲取模組資料失敗');
          router.push({ name: 'ModuleList' });
        }
      } catch (error) {
        console.error('獲取模組資料失敗:', error);
        
        let errorMessage = '獲取模組資料失敗，請稍後再試';
        if (error.code === 'NOT_FOUND') {
          errorMessage = '模組不存在或已被刪除';
        } else if (error.response?.status === 404) {
          errorMessage = '模組不存在';
        } else if (error.response?.status === 401) {
          errorMessage = '未授權，請重新登入';
        } else if (error.response?.status === 403) {
          errorMessage = '您沒有權限查看此模組';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        await showError('獲取失敗', errorMessage);
        router.push({ name: 'ModuleList' });
      } finally {
        loading.value = false;
      }
    };

    // 載入模組關聯的程式列表
    const fetchAssociatedPrograms = async () => {
      if (!route.params.id) return;

      loadingPrograms.value = true;
      programsLoadFailed.value = false;
      try {
        const response = await moduleProgramService.getProgramsByModuleId(route.params.id);
        // axios 攔截器已經返回 response.data，所以這裡直接檢查 success
        if (response && response.success) {
          associatedPrograms.value = response.data || [];
        } else {
          programsLoadFailed.value = true;
          associatedPrograms.value = [];
        }
      } catch (error) {
        console.error('載入關聯程式失敗:', error);
        programsLoadFailed.value = true;
        associatedPrograms.value = [];
      } finally {
        loadingPrograms.value = false;
      }
    };

    // 分頁載入全部啟用程式（API pageSize 上限 100）
    const fetchAllActivePrograms = async () => {
      const pageSize = 100;
      let page = 1;
      let hasNext = true;
      const allItems = [];

      while (hasNext) {
        const response = await programService.getPrograms({
          status: 'active',
          page,
          pageSize
        });
        const items = response?.data?.items || [];
        allItems.push(...items);
        hasNext = response?.data?.pagination?.hasNext === true;
        page += 1;
        if (page > 50) {
          break;
        }
      }

      return allItems;
    };

    // 載入可選的程式列表（以草稿清單排除，新增／編輯共用）
    const fetchAvailablePrograms = async () => {
      loadingAvailablePrograms.value = true;
      try {
        const items = await fetchAllActivePrograms();
        const associatedIds = new Set(associatedPrograms.value.map((program) => program.id));
        availablePrograms.value = items.filter((program) => !associatedIds.has(program.id));
      } catch (error) {
        console.error('載入可選程式失敗:', error);
        await showError('載入失敗', error.message || '載入可選程式失敗');
      } finally {
        loadingAvailablePrograms.value = false;
      }
    };

    // 打開添加程式對話框
    const openAddProgramsDialog = async () => {
      if (programsLoadFailed.value) {
        await showError('無法添加', '程式功能載入失敗，請重新整理後再試');
        return;
      }
      await fetchAvailablePrograms();
      showAddProgramsDialog.value = true;
    };

    // 格式化權限名稱（例如：member:read -> 讀取）
    const formatPermissionName = (permission) => {
      const parts = permission.split(':');
      if (parts.length !== 2) return permission;
      
      const actionMap = {
        'read': '讀取',
        'create': '新增',
        'update': '編輯',
        'delete': '刪除',
        'export': '匯出',
        'print': '列印'
      };
      
      const resource = parts[0];
      const action = parts[1];
      
      return `${resource}:${actionMap[action] || action}`;
    };

    // 檢查是否所有權限都被勾選
    const isAllPermissionsSelected = (program) => {
      // 如果 allowedPermissions 為 null 或空，表示允許所有功能
      if (!program.allowedPermissions || program.allowedPermissions.length === 0) {
        return true;
      }
      
      // 如果程式沒有權限定義，返回 true
      if (!program.permissions || program.permissions.length === 0) {
        return true;
      }
      
      // 檢查 allowedPermissions 是否包含所有 program.permissions
      const allowedSet = new Set(program.allowedPermissions);
      const programSet = new Set(program.permissions);
      
      // 如果兩個集合的大小相同，且 allowedPermissions 包含所有 program.permissions
      return allowedSet.size === programSet.size && 
             Array.from(programSet).every(perm => allowedSet.has(perm));
    };

    // 打開編輯權限對話框
    const handleEditPermissions = (program) => {
      editingProgram.value = program;
      // 初始化選中的權限
      if (program.allowedPermissions && program.allowedPermissions.length > 0) {
        editPermissionsSelected.value = [...program.allowedPermissions];
      } else {
        // 如果沒有設定權限（null），預設勾選 read（如果程式有 read 權限）
        if (program.permissions && program.permissions.length > 0) {
          const readPermission = program.permissions.find(p => p.endsWith(':read'));
          editPermissionsSelected.value = readPermission ? [readPermission] : [];
        } else {
          editPermissionsSelected.value = [];
        }
      }
      showEditPermissionsDialog.value = true;
    };

    // 切換編輯權限
    const handleEditPermissionToggle = (permission) => {
      // 如果當前是 null（允許全部功能），需要先初始化為所有權限
      if (editPermissionsSelected.value === null) {
        if (editingProgram.value && editingProgram.value.permissions) {
          editPermissionsSelected.value = [...editingProgram.value.permissions];
        } else {
          editPermissionsSelected.value = [];
        }
      }
      
      if (!Array.isArray(editPermissionsSelected.value)) {
        editPermissionsSelected.value = [];
      }
      
      const perms = editPermissionsSelected.value;
      const index = perms.indexOf(permission);
      
      // 如果是 read 權限且是唯一選中的權限，不允許取消
      if (index > -1 && permission.endsWith(':read') && perms.length === 1) {
        return; // 不允許取消最後一個 read 權限
      }
      
      if (index > -1) {
        perms.splice(index, 1);
        // 如果取消後沒有任何權限，至少保留 read（如果程式有 read 權限）
        if (perms.length === 0) {
          if (editingProgram.value && editingProgram.value.permissions) {
            const readPermission = editingProgram.value.permissions.find(p => p.endsWith(':read'));
            if (readPermission) {
              perms.push(readPermission);
            }
          }
        }
      } else {
        perms.push(permission);
      }
    };

    // 全選編輯權限
    const selectAllEditPermissions = () => {
      if (editingProgram.value && editingProgram.value.permissions) {
        editPermissionsSelected.value = [...editingProgram.value.permissions];
      }
    };

    // 允許全部功能（清除限制）
    const clearAllEditPermissions = () => {
      editPermissionsSelected.value = null;
    };

    // 套用權限到草稿（建立／更新模組時才寫入）
    const handleSavePermissions = async () => {
      if (!editingProgram.value) return;

      let allowedPermissions;
      if (editPermissionsSelected.value === null) {
        allowedPermissions = null;
      } else if (Array.isArray(editPermissionsSelected.value) && editPermissionsSelected.value.length > 0) {
        allowedPermissions = [...editPermissionsSelected.value];
      } else if (editingProgram.value.permissions && editingProgram.value.permissions.length > 0) {
        const readPermission = editingProgram.value.permissions.find(p => p.endsWith(':read'));
        allowedPermissions = readPermission ? [readPermission] : null;
      } else {
        allowedPermissions = null;
      }

      const targetId = editingProgram.value.id;
      associatedPrograms.value = associatedPrograms.value.map((program) => {
        if (program.id !== targetId) {
          return program;
        }
        return {
          ...program,
          allowedPermissions
        };
      });

      showEditPermissionsDialog.value = false;
      editingProgram.value = null;
      editPermissionsSelected.value = [];
    };

    // 添加程式到草稿清單（建立／更新模組時才寫入）
    const handleAddPrograms = async (data) => {
      if (!data || !data.programIds || data.programIds.length === 0) return;

      addingPrograms.value = true;
      try {
        const selectedPrograms = data.programIds.map((programId) => {
          const program = availablePrograms.value.find((item) => item.id === programId);
          if (!program) {
            return null;
          }

          let allowedPermissions = null;
          if (data.programPermissions && Object.prototype.hasOwnProperty.call(data.programPermissions, programId)) {
            const perms = data.programPermissions[programId];
            if (perms === null) {
              allowedPermissions = null;
            } else if (perms && perms.length > 0) {
              allowedPermissions = [...perms];
            } else if (program.permissions) {
              const readPermission = program.permissions.find((p) => p.endsWith(':read'));
              allowedPermissions = readPermission ? [readPermission] : null;
            }
          }

          return {
            ...program,
            allowedPermissions
          };
        }).filter(Boolean);

        associatedPrograms.value = appendProgramsToDraft(associatedPrograms.value, selectedPrograms);
        showAddProgramsDialog.value = false;
      } finally {
        addingPrograms.value = false;
      }
    };

    // 從草稿清單移除程式（建立／更新模組時才寫入）
    const handleRemoveProgram = (programId) => {
      associatedPrograms.value = associatedPrograms.value.filter((program) => program.id !== programId);
    };

    const onProgramDragStart = (index) => {
      programDragIndex.value = index;
    };

    const onProgramDragOver = (index) => {
      programDragOverIndex.value = index;
    };

    const onProgramDragLeave = () => {
      programDragOverIndex.value = null;
    };

    const onProgramDrop = (toIndex) => {
      const fromIndex = programDragIndex.value;
      if (fromIndex === null) {
        return;
      }
      associatedPrograms.value = reorderProgramsInDraft(associatedPrograms.value, fromIndex, toIndex);
      programDragIndex.value = null;
      programDragOverIndex.value = null;
    };

    const onProgramDragEnd = () => {
      programDragIndex.value = null;
      programDragOverIndex.value = null;
    };

    // 監聽對話框顯示，載入可選程式
    watch(showAddProgramsDialog, async (newValue) => {
      if (newValue) {
        await fetchAvailablePrograms();
      }
    });

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      // 新增模組時，必須通過代碼驗證才能送出
      if (!isEdit.value) {
        if (codeValidation.isChecking) {
          await showError('請稍候', '模組代碼驗證中，請等待驗證完成後再送出');
          return;
        }
        if (!codeValidation.checked || !codeValidation.isValid) {
          await showError('請先驗證模組代碼', '請在「模組代碼」欄位輸入後，點擊其他地方離開欄位，待出現「此代碼可以使用」後再進行新增');
          return;
        }
      }

      if (isEdit.value && programsLoadFailed.value) {
        await showError('無法更新', '程式功能載入失敗，請重新整理後再更新，以免清空既有設定');
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      try {
        const moduleData = {
          moduleName: form.moduleName.trim(),
          visibleAccountTypes: form.visibleAccountTypes,
          isOptional: form.isOptional,
          status: form.status,
          description: form.description.trim(),
          programs: buildModuleProgramsPayload(associatedPrograms.value)
        };

        // 只有新增時才包含 moduleCode
        if (!isEdit.value) {
          moduleData.moduleCode = form.moduleCode.trim();
        }

        // 只有當有值時才加入
        if (form.moduleNameEn) {
          moduleData.moduleNameEn = form.moduleNameEn.trim();
        }
        if (form.descriptionEn) {
          moduleData.descriptionEn = form.descriptionEn.trim();
        }
        if (form.icon) {
          moduleData.icon = form.icon.trim();
        }
        if (form.sortOrder !== null && form.sortOrder !== undefined && form.sortOrder !== '') {
          moduleData.sortOrder = Number(form.sortOrder);
        }
        
        // 價格欄位（僅選購模組，且必須明確設置）
        if (form.isOptional) {
          // 處理月費：僅在有有效值時提交（不提交 null），轉換為整數
          if (form.monthlyPrice !== null && form.monthlyPrice !== undefined && form.monthlyPrice !== '' && !isNaN(form.monthlyPrice)) {
            const monthlyPrice = Math.floor(Number(form.monthlyPrice));
            if (monthlyPrice >= 0) {
              moduleData.monthlyPrice = monthlyPrice;
            }
          }
          // 處理年費：僅在有有效值時提交（不提交 null），轉換為整數
          if (form.yearlyPrice !== null && form.yearlyPrice !== undefined && form.yearlyPrice !== '' && !isNaN(form.yearlyPrice)) {
            const yearlyPrice = Math.floor(Number(form.yearlyPrice));
            if (yearlyPrice >= 0) {
              moduleData.yearlyPrice = yearlyPrice;
            }
          }
        }
        // 注意：非選購模組不提交價格欄位（schema 有 additionalProperties: false）

        // 調試：輸出提交的數據
        console.log('提交的模組數據:', JSON.stringify(moduleData, null, 2));

        let response;
        if (isEdit.value) {
          response = await moduleService.update(route.params.id, moduleData);
        } else {
          response = await moduleService.create(moduleData);
        }

        if (response && response.success) {
          await showSuccess(
            isEdit.value ? '更新成功' : '新增成功',
            `模組「${moduleData.moduleName}」已成功${isEdit.value ? '更新' : '建立'}`
          );
          // 返回列表頁，如果有 returnPage 參數則恢復頁碼
          const returnPage = route.query.returnPage;
          if (returnPage) {
            router.push({ name: 'ModuleList', query: { page: returnPage } });
          } else {
            router.push({ name: 'ModuleList' });
          }
        } else {
          let errorTitle = isEdit.value ? '更新失敗' : '新增失敗';
          let errorText = response?.message || `${isEdit.value ? '更新' : '新增'}模組失敗`;

          if (response?.error?.code === 'MODULE_CONFLICT_001') {
            errors.moduleCode = '模組代碼已存在';
            errorText = '模組代碼已存在';
          }
          await showError(errorTitle, errorText);
        }
      } catch (error) {
        console.error(`${isEdit.value ? '更新' : '新增'}模組失敗:`, error);
        console.error('錯誤詳情:', {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          data: error.data || error.response?.data,
          details: error.details,
          fullError: error
        });
        
        let errorMessage = `${isEdit.value ? '更新' : '新增'}模組失敗，請稍後再試`;
        if (error.code === 'MODULE_CONFLICT_001' || error.code === 'CONFLICT') {
          errorMessage = '模組代碼已存在，請使用其他代碼';
        } else if (error.code === 'MODULE_NOT_FOUND_001' || error.code === 'NOT_FOUND') {
          errorMessage = '模組不存在或已被刪除';
        } else if (error.code === 'FORBIDDEN') {
          errorMessage = '您沒有權限執行此操作';
        } else if (error.response?.status === 401) {
          errorMessage = '未授權，請重新登入';
        } else if (error.response?.status === 403) {
          errorMessage = '您沒有權限執行此操作';
        } else if (error.response?.status === 400) {
          // 處理驗證錯誤
          const errorData = error.data || error.response?.data;
          console.log('驗證錯誤數據:', errorData);
          
          if (error.code === 'VALIDATION_ERROR') {
            // 嘗試從多個位置獲取驗證錯誤
            const validationErrors = 
              error.details?.validationErrors || 
              errorData?.error?.details?.validationErrors ||
              errorData?.error?.details?.validation ||
              [];
            
            console.log('驗證錯誤列表:', validationErrors);
            
            if (validationErrors.length > 0) {
              const errorMessages = validationErrors.map(err => {
                const field = (err.field || err.instancePath || err.dataPath || 'unknown')
                  .replace('/body/', '')
                  .replace('/', '');
                return `${field}: ${err.message || '驗證失敗'}`;
              });
              errorMessage = `輸入資料驗證失敗：\n${errorMessages.join('\n')}`;
            } else {
              errorMessage = errorData?.message || error.message || '輸入資料驗證失敗，請檢查輸入內容';
            }
          } else {
            errorMessage = errorData?.message || error.message || '輸入資料驗證失敗，請檢查輸入內容';
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        await showError(
          isEdit.value ? '更新失敗' : '新增失敗',
          errorMessage
        );
      } finally {
        loading.value = false;
      }
    };

    const handleCancel = () => {
      // 返回列表頁，如果有 returnPage 參數則恢復頁碼
      const returnPage = route.query.returnPage;
      if (returnPage) {
        router.push({ name: 'ModuleList', query: { page: returnPage } });
      } else {
        router.push({ name: 'ModuleList' });
      }
    };

    onMounted(() => {
      if (isEdit.value) {
        fetchModule();
      }
    });

    return {
      form,
      errors,
      errorMessage,
      loading,
      isEdit,
      isClientModule,
      toggleAccountType,
      codeValidation,
      handleModuleCodeInput,
      handleModuleCodeBlur,
      handleSubmit,
      handleCancel,
      // 程式功能管理
      loadingPrograms,
      programsLoadFailed,
      associatedPrograms,
      showAddProgramsDialog,
      loadingAvailablePrograms,
      availablePrograms,
      addingPrograms,
      programDragIndex,
      programDragOverIndex,
      openAddProgramsDialog,
      formatPermissionName,
      isAllPermissionsSelected,
      handleAddPrograms,
      handleRemoveProgram,
      onProgramDragStart,
      onProgramDragOver,
      onProgramDragLeave,
      onProgramDrop,
      onProgramDragEnd,
      // 編輯權限
      showEditPermissionsDialog,
      editingProgram,
      editPermissionsSelected,
      handleEditPermissions,
      handleEditPermissionToggle,
      selectAllEditPermissions,
      clearAllEditPermissions,
      handleSavePermissions,
    };
  },
};
</script>

<style scoped>
.create-module-container {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.form-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #e9ecef;
}

.form-row {
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
}

.form-group {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.full-width {
  flex: 1 1 100%;
}

.form-group label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 8px;
  font-size: 14px;
}

.form-group label.required::after {
  content: '*';
  color: #dc3545;
  margin-left: 4px;
}

.form-control {
  padding: 10px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-control:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.form-control.is-valid {
  border-color: #28a745;
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.form-hint {
  font-size: 12px;
  color: #6c757d;
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  min-height: 38px;
}

.form-group .checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  cursor: pointer;
  font-weight: normal;
  line-height: 1.25;
}

.form-group .form-checkbox {
  width: 16px;
  height: 16px;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
  cursor: pointer;
}

.form-hint-important {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #856404;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 8px 12px;
  margin-top: 8px;
  margin-bottom: 0;
}

.form-hint-important i {
  font-size: 16px;
  color: #ff9800;
  flex-shrink: 0;
}

.form-hint-important span {
  line-height: 1.4;
}

.invalid-feedback {
  display: block;
  color: #dc3545;
  font-size: 14px;
  margin-top: 6px;
}

.validation-feedback {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 14px;
}

.validation-feedback.checking {
  color: #6c757d;
}

.validation-feedback.valid {
  color: #28a745;
}

.validation-feedback.invalid {
  color: #dc3545;
}

.validation-feedback .spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.alert {
  padding: 12px 16px;
  margin-bottom: 20px;
  border: 1px solid transparent;
  border-radius: 4px;
}

.alert-danger {
  color: #721c24;
  background-color: #f8d7da;
  border-color: #f5c6cb;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #545b62;
}

textarea.form-control {
  resize: vertical;
  min-height: 80px;
}

select.form-control {
  cursor: pointer;
}

/* 程式功能管理樣式 */
.programs-management {
  margin-top: 16px;
}

.programs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.programs-description {
  color: #6c757d;
  font-size: 14px;
  margin: 0;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #6c757d;
  font-size: 14px;
}

.loading-state i,
.empty-state i {
  font-size: 48px;
  margin-bottom: 12px;
  color: #adb5bd;
}

.programs-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.program-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #f8f9fa;
  transition: all 0.2s;
  cursor: grab;
}

.program-item:hover {
  border-color: #ced4da;
  background: #fff;
}

.program-item.dragging {
  opacity: 0.4;
  background: #e0e7ff;
}

.program-item.drag-over {
  background: #eff6ff;
  border-top: 2px solid #667eea;
}

.drag-handle {
  font-size: 18px;
  color: #9ca3af;
  cursor: grab;
  flex-shrink: 0;
}

.drag-handle:active {
  cursor: grabbing;
}

.program-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.btn-outline-primary {
  background: white;
  color: #007bff;
  border: 1px solid #007bff;
}

.btn-outline-primary:hover:not(:disabled) {
  background: #007bff;
  color: white;
}

.program-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.program-icon {
  font-size: 24px;
  color: #495057;
}

.program-details {
  flex: 1;
}

.program-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.program-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #6c757d;
  align-items: center;
  flex-wrap: wrap;
}

.program-code {
  padding: 2px 8px;
  background: #e9ecef;
  border-radius: 3px;
  font-family: monospace;
}

.program-path-small {
  color: #868e96;
  font-size: 11px;
}

.program-description {
  margin-top: 6px;
  font-size: 13px;
  color: #495057;
  line-height: 1.5;
}

.program-allowed-permissions {
  margin-top: 8px;
  width: 100%;
}

.allowed-perms {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.perms-label {
  font-size: 12px;
  color: #495057;
  font-weight: 500;
}

.perm-badge {
  display: inline-block;
  padding: 3px 10px;
  background: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.all-perms-allowed {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  width: fit-content;
}

.all-perms-allowed i {
  font-size: 14px;
}

.program-permissions-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.perm-badge-small {
  padding: 2px 6px;
  background: #667eea;
  color: white;
  border-radius: 3px;
  font-size: 10px;
  font-family: monospace;
}

.program-permission {
  padding: 2px 8px;
  background: #d1ecf1;
  color: #0c5460;
  border-radius: 3px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

/* 對話框樣式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #f8f9fa;
  color: #333;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

.search-box {
  margin-bottom: 16px;
}

.available-programs-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
}

.available-program-item-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.available-program-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.available-program-item:hover {
  border-color: #007bff;
  background: #f8f9ff;
}

.available-program-item.selected {
  border-color: #007bff;
  background: #e7f3ff;
}

.available-program-item input[type="checkbox"] {
  cursor: pointer;
}

/* 權限選擇器樣式 */
.permissions-selector {
  padding: 12px;
  margin-left: 36px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.permissions-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #dee2e6;
}

.permissions-label {
  font-weight: 600;
  color: #495057;
  font-size: 14px;
}

.btn-link {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  font-size: 13px;
  padding: 2px 8px;
  text-decoration: none;
  transition: color 0.2s;
}

.btn-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

.permissions-checkboxes {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.permission-checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
}

.permission-checkbox-label:hover {
  border-color: #007bff;
  background: #f8f9ff;
}

.permission-checkbox-label input[type="checkbox"] {
  cursor: pointer;
  margin: 0;
}

.permission-name {
  user-select: none;
  color: #495057;
}

.permissions-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6c757d;
  font-style: italic;
}

.permissions-hint i {
  font-size: 14px;
}

.program-path {
  color: #6c757d;
  font-size: 12px;
  margin-left: 8px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: #c82333;
}

/* 編輯權限對話框樣式 */
.editing-program-info {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.program-icon-large {
  font-size: 32px;
  color: #007bff;
}

.program-name-large {
  font-size: 18px;
  font-weight: 600;
  color: #212529;
  margin-bottom: 4px;
}

.program-meta-large {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.program-description-large {
  margin-top: 8px;
  font-size: 14px;
  color: #495057;
  line-height: 1.6;
}

.permissions-editor {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.no-permissions {
  padding: 24px;
  text-align: center;
  color: #6c757d;
  background: #f8f9fa;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.no-permissions i {
  font-size: 20px;
}

@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
  }
}

</style>
